import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  AddressType,
  AddressTypes,
  CRS,
  CRSMappings,
  CRSValues,
  Dataset,
  Datasets,
  State,
  States,
} from "./models";
import { PredictiveAddressManager } from "./predictive-address-manager";
import { TruncatedText } from "components/shared/truncated-text";

interface MultiSelectProps<T extends string> {
  label: string;
  options: readonly T[];
  value: T[];
  onChange: (next: T[]) => void;
}

function MultiSelect<T extends string>({
  label,
  options,
  value,
  onChange,
}: MultiSelectProps<T>) {
  const toggle = (option: T) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "border-input bg-input/20 dark:bg-input/30 hover:bg-input/40 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 h-7 rounded-md border px-2 py-0.5 text-sm flex w-full items-center justify-between gap-2 outline-none focus-visible:ring-2",
            )}
          >
            <TruncatedText
              as="span"
              className="min-w-0 text-left"
              tooltip={value.join(", ")}
            >
              {value.length > 0 ? (
                value.join(", ")
              ) : (
                <span className="text-muted-foreground">Select...</span>
              )}
            </TruncatedText>
            <ChevronDown className="text-muted-foreground size-4 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-(--radix-popover-trigger-width)"
        >
          <div className="space-y-2 overflow-y-auto">
            {options.map((option) => (
              <div key={option} className="flex items-center gap-2">
                <Checkbox
                  id={`${label}-${option}`}
                  checked={value.includes(option)}
                  onCheckedChange={() => toggle(option)}
                />
                <Label
                  htmlFor={`${label}-${option}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {option}
                </Label>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export const DemoControls = (predictiveManager: PredictiveAddressManager) => {
  return (
    <div>
      <CardContent>
        <div className="grid gap-x-2 gap-y-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          <div className="space-y-1.5">
            <Label>
              Maximum Number of Results:
              <span className="font-semibold">
                {predictiveManager.maxResults}
              </span>
            </Label>
            <Slider
              value={[predictiveManager.maxResults]}
              min={1}
              max={20}
              step={1}
              onValueChange={([v]) => predictiveManager.setMaxResults(v)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Client De-bounce:
              <span className="font-semibold">
                {predictiveManager.debounce}ms
              </span>
            </Label>
            <Slider
              value={[predictiveManager.debounce]}
              min={100}
              max={1000}
              step={100}
              onValueChange={([v]) => predictiveManager.setDebounce(v)}
            />
          </div>
          <MultiSelect<Dataset>
            label="Dataset Filter"
            options={Datasets}
            value={predictiveManager.dataset}
            onChange={predictiveManager.setDataset}
          />
          <div className="space-y-1.5">
            <Label>CRS</Label>
            <Select
              value={predictiveManager.crs}
              onValueChange={(v) => predictiveManager.setCrs(v as CRS)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRSValues.map((crs) => (
                  <SelectItem key={crs} value={crs}>
                    {CRSMappings.get(crs)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <MultiSelect<State>
            label="State Filter"
            options={States}
            value={predictiveManager.stateFilter}
            onChange={predictiveManager.setStateFilter}
          />

          <MultiSelect<AddressType>
            label="Address Type Filter"
            options={AddressTypes}
            value={predictiveManager.addressType}
            onChange={predictiveManager.setAddressType}
          />
          <div className="flex items-end mb-1.5 gap-2">
            <Switch
              id="exclude-aliases"
              checked={predictiveManager.excludeAliases}
              onCheckedChange={predictiveManager.setExcludeAliases}
            />
            <Label htmlFor="exclude-aliases">Exclude Aliases</Label>
          </div>
          <div className="flex items-end mb-1.5 gap-2">
            <Switch
              id="exclude-principal-with-sec"
              checked={predictiveManager.excludePrincipalWithSec}
              onCheckedChange={predictiveManager.setExcludePrincipalWithSec}
            />
            <Label htmlFor="exclude-principal-with-sec">
              Exclude principal with secondaries
            </Label>
          </div>
        </div>
      </CardContent>
    </div>
  );
};
